import { usePickupModals } from "@redotech/redo-customer-portal-shared/hooks/useReturnConfirmation/use-pickup-modals";
import {
  StepIdentifierEnum,
  StepType,
} from "@redotech/redo-customer-portal-shared/hooks/useReturnConfirmation/use-steps";
import { useTranslation } from "@redotech/redo-i18n";
import CheckCircleFilled from "@redotech/redo-icons/check-circle_filled.svg";
import PlaceHolder from "@redotech/redo-icons/placeholder_filled.svg";
import { Resource } from "@redotech/redo-model/localization/resource";
import { Flex } from "@redotech/redo-web/flex";
import { Icon } from "@redotech/redo-web/icon";
import { Text } from "@redotech/redo-web/text";
import { assertNever } from "@redotech/util/type";
import { ReactElement } from "react";
import { BlueYonderQRCard } from "./blue-yonder-qr-card";
import { DropoffLabelButtons } from "./dropoffs/dropoff-label-buttons";
import { InStoreLocationsButton } from "./in-store-locations";
import { InStorePackingSlipButton } from "./in-store-packing-slip-button";
import { LabelButtons } from "./label-buttons";
import { PickupButtons } from "./pickup-buttons";
import { ReturnLocationsButton } from "./return-locations-button";
import { ReturnBearLabelButtons } from "./returnbear/return-bear-label-buttons";

interface IReturnStepsCardProps {
  returnId: string;
  copyResource: Resource | undefined;
  shouldAutoOpenBoxlessLocations?: boolean;
  shouldAutoOpenFedexLocations?: boolean;
  steps: StepType[] | undefined;
  pickupModals: ReturnType<typeof usePickupModals>;
}

export function ReturnStepsCard({
  returnId,
  copyResource,
  shouldAutoOpenBoxlessLocations = false,
  shouldAutoOpenFedexLocations = false,
  steps,
  pickupModals,
}: IReturnStepsCardProps) {
  const { t } = useTranslation();
  const stepsTitle = copyResource?.STATUS_STEPS_TITLE;

  function getStepComponent(step: StepType): ReactElement | null {
    switch (step.type) {
      case StepIdentifierEnum.DROP_OFF_PACKAGE:
        return <ReturnLocationsButton trackable={step.trackable} />;
      case StepIdentifierEnum.BLUE_YONDER_DROP_OFF:
        return (
          <BlueYonderQRCard
            autoOpen={
              shouldAutoOpenFedexLocations || shouldAutoOpenBoxlessLocations
            }
            blueYonderQRCodes={step.blueYonderQRCodes}
            returnId={returnId}
          />
        );
      case StepIdentifierEnum.RETURN_BEAR_SHIPPING:
        return (
          <ReturnBearLabelButtons
            autoOpen={shouldAutoOpenBoxlessLocations}
            returnBearDetails={step.returnBearDetails}
            returnId={returnId}
          />
        );
      case StepIdentifierEnum.DROPOFF:
        return (
          <DropoffLabelButtons
            autoOpen={shouldAutoOpenBoxlessLocations}
            customerCountry={step.customerCountry}
            dropoffs={step.dropoffs}
            returnId={returnId}
          />
        );
      case StepIdentifierEnum.IN_STORE_PRINT_PACKING_SLIP:
        return (
          <InStorePackingSlipButton packingSlipLink={step.packingSlipLink} />
        );
      case StepIdentifierEnum.IN_STORE_RETURN:
        return <InStoreLocationsButton returnId={returnId} />;
      case StepIdentifierEnum.PICKUP_PRINT_LABEL:
      case StepIdentifierEnum.SHIPMENT_TRANSIT:
        return step.shipmentInfo ? (
          <LabelButtons
            carrier={step.shipmentInfo.carrier}
            commercialInvoice={step.shipmentInfo.commercialInvoice}
            qrCodeOnly={step.shipmentInfo.qrCodeOnly}
            trackable={step.shipmentInfo.trackable}
          />
        ) : null;
      case StepIdentifierEnum.PICKUP_MANAGE:
        return <PickupButtons pickupModals={pickupModals} />;
      case StepIdentifierEnum.EXCHANGE_ORDER:
      case StepIdentifierEnum.GREEN_RETURN_INSTRUCTIONS:
      case StepIdentifierEnum.GREEN_RETURN_PROCESSED:
      case StepIdentifierEnum.HAPPY_RETURNS_DROP_OFF:
      case StepIdentifierEnum.HAPPY_RETURNS_PROCESSED:
      case StepIdentifierEnum.IN_STORE_PROCESSED:
      case StepIdentifierEnum.MANUAL_REVIEW:
      case StepIdentifierEnum.NONE:
      case StepIdentifierEnum.PICKUP_ATTACH_LABEL:
      case StepIdentifierEnum.PROCESSED:
      case StepIdentifierEnum.PROCESSED_REPAIR: //TODO: TrackRepairButton
      case StepIdentifierEnum.SHIP_BACK_REPAIR:
      case StepIdentifierEnum.WARNING:
        return null;
      default:
        assertNever(step);
    }
  }
  return (
    <Flex
      data-ui="return-steps"
      flexDirection="column"
      gap="3xl"
      pb="sm"
      pt="sm"
    >
      <Text fontSize="lg" fontWeight="semibold">
        {stepsTitle === "What happens next?"
          ? t("What happens next?")
          : (stepsTitle ?? t("What happens next?"))}
      </Text>
      <Flex data-ui="steps-list" flexDirection="column">
        {steps?.map((step: StepType, i) => {
          return (
            <Flex data-ui="step-item" flexDirection="row" gap="sm" key={i}>
              <Flex align="center" flexDirection="column">
                <Flex flexDirection="column">
                  <Icon
                    arbiterIconSvg={(props) =>
                      step.complete
                        ? CheckCircleFilled(props)
                        : PlaceHolder(props)
                    }
                    color={step.complete ? "success" : "light"}
                    size="compact"
                  />
                </Flex>
                {i != steps.length - 1 && (
                  <Flex
                    flex={1}
                    flexDirection="column"
                    pb="xxs"
                    pt="xxs"
                    style={{
                      borderColor: "#E5E5E5",
                      borderWidth: 1,
                      borderRadius: 12,
                      borderStyle: "solid",
                      width: "2px",
                    }}
                  />
                )}
              </Flex>

              <Flex flex={1} flexDirection="column" pb="sm">
                <Text fontSize="md" fontWeight="medium">
                  {step.title}
                </Text>
                <Text fontSize="sm" fontWeight="regular">
                  {step.subtitle}
                </Text>
                {getStepComponent(step)}
              </Flex>
            </Flex>
          );
        })}
      </Flex>
    </Flex>
  );
}
